import dayjs, { Dayjs } from "utils/dayjs"

import ApiService from "./ApiService"
import { ApiServiceName, ApiLinkEntity, ApiLinkRel, ErrorCode } from "config/constants"
import CustomError from "models/CustomError"
import { AxiosError } from "axios"
import InterfaceStatus, { IGetInterfaceStatusDto } from "models/InterfaceStatus"
import InvoiceByOrderNumberLookup, { IInvoiceByOrderNumberDto } from "models/InvoiceByOrderNumberLookup"
import ErrorHandler from "utils/errorHandler"
import { IApiLink } from "@/types"

export default class DataService {

    static async getZtsSync(): Promise<{ lastSyncDate?: Dayjs, isInProgress: boolean }> {
        try {
            const resp: { lastSyncDate?: string, isInProgress: boolean } = await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.ztsLastSyncState,
                    rel: ApiLinkRel.self
                },
            })
            return {
                lastSyncDate: resp.lastSyncDate ? dayjs(resp.lastSyncDate) : undefined,
                isInProgress: resp.isInProgress,
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    static async makeZtsSync(): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.ztsSyncManual,
                    rel: ApiLinkRel.new
                },
            })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response && error.response.status === 409) {
                    throw new CustomError({
                        code: ErrorCode.conflict,
                    })
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    static async getInterfaceStatuses(): Promise<InterfaceStatus[]> {
        try {
            const resp: { entities: IGetInterfaceStatusDto[] } = await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.interfaceStatus,
                    rel: ApiLinkRel.all
                },
            })

            return resp.entities.map(dto => new InterfaceStatus(dto))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    static async refreshBlockedCustomersForOutgoingInvoicesToBeCreated(): Promise<{ checkStatusLink?: IApiLink } | undefined> {
        try {
            const resp: { links?: IApiLink[] | null } | undefined = await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.unblockCustomers,
                    rel: ApiLinkRel.new
                },
            })

            if (!resp) {
                return
            }

            return {
                checkStatusLink: resp.links?.find(link => link.entity === ApiLinkEntity.asyncOperationStatus && link.rel === ApiLinkRel.self)
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    static async refreshCustomer(customerNumber: string): Promise<{ checkStatusLink?: IApiLink } | undefined> {
        try {
            const resp: { links?: IApiLink[] | null } | undefined = await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.refreshCustomer,
                    rel: ApiLinkRel.new,
                    params: { customerNumber },
                    templated: true
                },
            })

            if (!resp) {
                return
            }

            return {
                checkStatusLink: resp.links?.find(link => link.entity === ApiLinkEntity.asyncOperationStatus && link.rel === ApiLinkRel.self)
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    static async getInvoiceByOrderNumber(orderNumber: string): Promise<InvoiceByOrderNumberLookup | null> {
        try {
            const dto: IInvoiceByOrderNumberDto = await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.invoiceByOrderNumber,
                    rel: ApiLinkRel.self,
                    params: {
                        query: {
                            ordernumber: orderNumber
                        }
                    },
                    templated: false
                },
            })
            return new InvoiceByOrderNumberLookup(dto)
        } catch (e) {
            if (e?.isAxiosError && (e as AxiosError<any>).response?.status === 404) {
                return null
            }
            ErrorHandler.handleHttpError(e)
        }
    }

    static async getCmmText(id: string, language: string): Promise<{ text: string }> {
        try {
            const resp: { text: string } = await ApiService.request({
                serviceName: ApiServiceName.data,
                followLink: {
                    entity: ApiLinkEntity.cmmText,
                    rel: ApiLinkRel.self,
                    params: { id },
                    query: { language }
                },
            })
            return resp
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }
}
